import React, { useState } from "react";
import { Type, Plus, X, Trash2 } from "lucide-react";

/**
 * Reusable Font Selector Component
 * Manages brand fonts list
 */
export default function FontSelector({ fonts = [], onChange, title = "Brand Fonts" }) {
  const [addingNew, setAddingNew] = useState(false);
  const [newFont, setNewFont] = useState("");

  const handleAdd = () => {
    if (newFont.trim() && !fonts.includes(newFont.trim())) {
      onChange([...fonts, newFont.trim()]);
      setNewFont("");
      setAddingNew(false);
    }
  };

  const handleRemove = (index) => {
    const updated = fonts.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAdd();
    } else if (e.key === "Escape") {
      setNewFont("");
      setAddingNew(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {title} ({fonts.length})
        </h3>
        <button
          onClick={() => setAddingNew(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Font
        </button>
      </div>

      {fonts.length === 0 && !addingNew ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <Type className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-2">No fonts defined yet</p>
          <p className="text-sm">Click "Add Font" to get started</p>
        </div>
      ) : (
        <div className="space-y-2">
          {fonts.map((font, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors bg-white"
            >
              <div className="flex items-center gap-3 flex-1">
                <Type className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{font}</p>
                  <p
                    className="text-xs text-gray-500 mt-1"
                    style={{ fontFamily: font }}
                  >
                    The quick brown fox jumps over the lazy dog
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRemove(index)}
                className="p-2 bg-gray-100 rounded hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          ))}

          {/* Add New Font Input */}
          {addingNew && (
            <div className="flex items-center gap-2 p-4 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50">
              <Type className="w-5 h-5 text-blue-600" />
              <input
                type="text"
                value={newFont}
                onChange={(e) => setNewFont(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="e.g., Roboto, sans-serif"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleAdd}
                disabled={!newFont.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
              <button
                onClick={() => {
                  setNewFont("");
                  setAddingNew(false);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

